import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Bell, Check, HeartHandshake, X as XIcon } from 'lucide-react';
import { CHAT_HOUR_PATH } from '../../content/chatHour';
import { MEET_AGAIN_PATH } from '../../content/meetAgainGuide.meta';
import { useReminders } from '../../hooks/useReminders';
import { MEET_NOTE_MAX, meetDetail, meetLabel, type MeetLocal, type MeetPairInfo } from '../../lib/meetAgain';

// The Meet Again icon (two hands and a heart), inline with text. Use it instead of an emoji.
export const MeetIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <HeartHandshake size={size} strokeWidth={2} aria-hidden="true" className={`inline-block align-[-0.15em] ${className}`} />
);

// What this person's own tap is doing. The other person's choice is never shown,
// only the moment both have tapped ('matched').
export type MeetTapState = 'idle' | 'waiting' | 'matched' | 'full' | 'early' | 'closed';

const serif = { fontFamily: "'Instrument Serif', serif" };

type SheetProps = {
  isNight: boolean;
  state: MeetTapState;
  busy: boolean;
  error: string | null;
  pairId?: string;                       // the pair made in this chat
  pairs: MeetPairInfo[];
  max: number;
  locals: Record<string, MeetLocal>;
  onTap: (on: boolean) => void;
  onLetGo: (pairId: string) => void;
  onNote: (pairId: string, note: string) => void;
  onClose: () => void;
};

// The Meet Again popup: how it works, the tap, your saved people, and the note after a match.
export function MeetAgainSheet(p: SheetProps) {
  const { isNight, onClose } = p;
  const [confirming, setConfirming] = useState<string | null>(null);
  const card = isNight ? 'bg-[#0d1b2a]/95 border-white/10 text-white shadow-black/50' : 'bg-white/95 border-black/10 text-black shadow-black/15';
  const primary = `w-full py-2.5 rounded-full text-sm font-semibold transition-all active:scale-[0.98] disabled:opacity-60 ${isNight ? 'bg-white text-black hover:bg-white/90' : 'bg-[#1a1a1a] text-white hover:bg-black/90'}`;
  const ghost = `rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${isNight ? 'border-white/20 hover:bg-white/10' : 'border-black/15 hover:bg-black/5'}`;
  const others = p.pairs.filter(x => x.id !== p.pairId);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const list = (rows: MeetPairInfo[]) => (
    <ul className="flex flex-col gap-1.5">
      {rows.map(pair => (
        <li key={pair.id} className={`flex items-center gap-3 rounded-2xl border px-3 py-2 ${isNight ? 'border-white/10' : 'border-black/10'}`}>
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-medium truncate">{meetLabel(p.locals[pair.id], pair)}</span>
            <span className="block text-[0.6875rem] opacity-60 truncate">{meetDetail(p.locals[pair.id], pair)}</span>
          </span>
          <button
            type="button"
            disabled={p.busy}
            onClick={() => { if (confirming === pair.id) { setConfirming(null); p.onLetGo(pair.id); } else setConfirming(pair.id); }}
            className={`${ghost} shrink-0 ${confirming === pair.id ? 'text-red-500 border-red-500/40' : ''}`}
          >
            {confirming === pair.id ? 'Sure? Let go' : 'Let go'}
          </button>
        </li>
      ))}
    </ul>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[180] flex items-end sm:items-center justify-center px-4 pb-4 sm:pb-0 bg-black/40 backdrop-blur-sm"
      onClick={p.onClose}
    >
      <motion.div
        role="dialog" aria-modal="true" aria-labelledby="meet-title"
        initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        onClick={e => e.stopPropagation()}
        className={`relative w-full max-w-sm max-h-[85vh] overflow-y-auto rounded-[28px] border p-6 shadow-2xl backdrop-blur-xl ${card}`}
      >
        <button type="button" aria-label="Close" onClick={p.onClose} className="absolute right-4 top-4 p-1.5 rounded-full opacity-60 hover:opacity-100">
          <XIcon size={18} />
        </button>

        {p.state === 'matched' && p.pairId ? (
          <Matched {...p} pairId={p.pairId} />
        ) : (
          <>
            <h2 id="meet-title" className="text-2xl italic pr-8" style={serif}>Meet again? <MeetIcon size={22} className={isNight ? 'text-emerald-300' : 'text-emerald-600'} /></h2>
            {p.state === 'full' ? (
              <>
                <p className="mt-2 text-sm opacity-80">Your {p.max} spots are full. Let one go to make room.</p>
                <div className="mt-4">{list(p.pairs)}</div>
              </>
            ) : p.state === 'closed' ? (
              <p className="mt-2 text-sm opacity-80">Meet Again isn't open for this chat anymore. You can tap Meet again in your next chat.</p>
            ) : (
              <>
                <ul className="mt-3 flex flex-col gap-1.5 text-sm opacity-80">
                  <li>If you both tap, Zuzu brings you together when you're both here.</li>
                  <li>If only you tap, nobody knows.</li>
                  <li>Saved on this device for 30 days.</li>
                </ul>
                <Link to={MEET_AGAIN_PATH} target="_blank" className="mt-2 inline-block text-xs font-medium underline underline-offset-4 opacity-70 hover:opacity-100">
                  See how it works
                </Link>
                <div className="mt-5">
                  {p.state === 'waiting' ? (
                    <div className="flex flex-col items-center gap-2 text-center">
                      <p className="text-sm font-medium inline-flex items-center gap-1.5"><MeetIcon className={isNight ? 'text-emerald-300' : 'text-emerald-600'} /> On. Only you can see this.</p>
                      <p className="text-xs opacity-60">If they tap too, you'll both know.</p>
                      <button type="button" disabled={p.busy} onClick={() => p.onTap(false)} className={`${ghost} mt-1`}>Turn off</button>
                    </div>
                  ) : p.state === 'early' ? (
                    <p className="text-sm text-center opacity-70">Say hello first. Meet Again opens once you've both written something.</p>
                  ) : (
                    <button type="button" autoFocus disabled={p.busy} onClick={() => p.onTap(true)} className={primary}>
                      <span className="inline-flex items-center gap-2">Meet again <MeetIcon /></span>
                    </button>
                  )}
                </div>
                {others.length > 0 && (
                  <div className="mt-6">
                    <p className="text-[0.6875rem] uppercase tracking-widest opacity-50 mb-2">Your Meet Again · {p.pairs.length} of {p.max}</p>
                    {list(others)}
                  </div>
                )}
              </>
            )}
          </>
        )}
        {p.error && <p role="alert" className="mt-4 text-xs text-center text-red-500">{p.error}</p>}
      </motion.div>
    </motion.div>
  );
}

// Both tapped: a note to remember them by, and the Chat Hour nudge.
function Matched(p: SheetProps & { pairId: string }) {
  const { isNight } = p;
  const [note, setNote] = useState(p.locals[p.pairId]?.note ?? '');
  const [confirm, setConfirm] = useState(false);
  const reminders = useReminders();
  const save = (v: string) => p.onNote(p.pairId, v.slice(0, MEET_NOTE_MAX));
  const field = `mt-2 w-full rounded-xl border px-3 py-2 text-sm bg-transparent outline-none focus:ring-2 ${isNight ? 'border-white/20 focus:ring-white/30 placeholder:text-white/40' : 'border-black/15 focus:ring-black/20 placeholder:text-black/40'}`;
  return (
    <div>
      <h2 id="meet-title" className="text-2xl italic pr-8" style={serif}>You'll meet again <MeetIcon size={22} className={isNight ? 'text-emerald-300' : 'text-emerald-600'} /></h2>
      <p className="mt-1 text-sm opacity-70">Zuzu will let you both know when you're here at the same time.</p>
      <label className="block mt-5 text-xs font-medium opacity-80" htmlFor="meet-note">Add a note so you remember them</label>
      <input
        id="meet-note" value={note} maxLength={MEET_NOTE_MAX} placeholder="the guitar person"
        onChange={e => { setNote(e.target.value); save(e.target.value); }}
        className={field}
      />
      <p className="mt-1 text-[0.6875rem] opacity-50">The other person never sees it.</p>
      <div className={`mt-5 flex items-center gap-3 rounded-2xl border px-3 py-2.5 ${isNight ? 'border-white/10' : 'border-black/10'}`}>
        <Bell size={16} className="shrink-0 opacity-70" />
        <p className="flex-1 text-xs opacity-80">Most people are here at Chat Hour.</p>
        {reminders.state === 'on' ? (
          <span className="shrink-0 inline-flex items-center gap-1 text-xs font-medium"><Check size={14} /> Reminder on</span>
        ) : reminders.state === 'idle' || reminders.state === 'working' ? (
          <button type="button" disabled={reminders.state === 'working'} onClick={() => reminders.turnOn()}
            className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium disabled:opacity-60 ${isNight ? 'border-white/20 hover:bg-white/10' : 'border-black/15 hover:bg-black/5'}`}>
            {reminders.state === 'working' ? 'Turning on…' : 'Remind me'}
          </button>
        ) : (
          <Link to={CHAT_HOUR_PATH} target="_blank" className="shrink-0 text-xs font-medium underline underline-offset-2">See times</Link>
        )}
      </div>
      <button type="button" onClick={p.onClose}
        className={`mt-5 w-full py-2.5 rounded-full text-sm font-semibold ${isNight ? 'bg-white text-black hover:bg-white/90' : 'bg-[#1a1a1a] text-white hover:bg-black/90'}`}>
        Done
      </button>
      <button type="button" disabled={p.busy}
        onClick={() => { if (confirm) p.onLetGo(p.pairId); else setConfirm(true); }}
        className={`mt-3 w-full text-xs ${confirm ? 'text-red-500' : 'opacity-50 hover:opacity-100'}`}>
        {confirm ? 'Sure? Tap again to let go. They won’t be told.' : 'Let go of this Meet Again'}
      </button>
    </div>
  );
}

// "🌻 The guitar person is here". Both must say yes within the time shown.
export function MeetOfferCard({ isNight, label, expiresIn, waiting, onAnswer }: {
  isNight: boolean; label: string; expiresIn: number; waiting: boolean;
  onAnswer: (accept: boolean) => void;
}) {
  const left = Math.max(1, expiresIn / 1000);
  return (
    <motion.div
      role="dialog" aria-live="polite" aria-label="Someone you wanted to meet again is here"
      initial={{ opacity: 0, y: -24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -24 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      // At the top, sliding down like a phone notification, so it never covers a page's main button.
      className={`fixed z-[165] left-1/2 -translate-x-1/2 top-[max(1rem,env(safe-area-inset-top))] w-[calc(100%-2rem)] max-w-sm overflow-hidden rounded-[28px] border p-5 shadow-2xl backdrop-blur-xl ${
        isNight ? 'bg-[#0d1b2a]/90 border-white/10 text-white shadow-black/50' : 'bg-white/95 border-black/10 text-black shadow-black/15'
      }`}
    >
      <div className="flex items-start gap-3">
        <span className="text-2xl leading-none mt-0.5" aria-hidden="true">🌻</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate">{label} is here</p>
          <p className="text-xs opacity-70 mt-1">You both wanted to meet again.</p>
        </div>
      </div>
      {waiting ? (
        <p className="mt-4 text-center text-xs opacity-70" role="status">Waiting for them…</p>
      ) : (
        <div className="flex gap-2 mt-4">
          <button onClick={() => onAnswer(true)}
            className={`flex-1 py-2.5 rounded-full text-xs font-semibold transition-all active:scale-95 ${isNight ? 'bg-white text-black hover:bg-white/90' : 'bg-[#1a1a1a] text-white hover:bg-black/90'}`}>
            Meet again
          </button>
          <button onClick={() => onAnswer(false)}
            className={`flex-1 py-2.5 rounded-full text-xs font-medium border transition-all ${isNight ? 'border-white/15 hover:bg-white/10' : 'border-black/10 hover:bg-black/5'}`}>
            Not now
          </button>
        </div>
      )}
      <motion.div aria-hidden="true" className={`absolute left-0 bottom-0 h-0.5 ${isNight ? 'bg-amber-200/50' : 'bg-amber-400/60'}`}
        initial={{ width: '100%' }} animate={{ width: '0%' }} transition={{ duration: left, ease: 'linear' }} />
    </motion.div>
  );
}

// A one-time tip in a person's first chat where Meet Again opens (only on this device).
export function MeetIntroCard({ isNight, canTap, onOpen }: { isNight: boolean; canTap: boolean; onOpen: () => void }) {
  const accent = isNight ? 'text-rose-300' : 'text-rose-500'; // like the button before it is tapped
  return (
    <div className={`max-w-[92%] rounded-2xl border px-4 py-3.5 text-left ${isNight ? 'bg-white/5 border-white/10 text-white/85' : 'bg-white/70 border-black/10 text-black/80'}`}>
      <div className="flex items-start gap-3">
        <MeetIcon size={18} className={`mt-0.5 shrink-0 ${accent}`} />
        <div className="text-[0.8125rem] leading-relaxed">
          <p className="font-semibold">Liked talking to them?</p>
          <p className="mt-1">Tap <MeetIcon size={14} className={accent} /> at the top to meet again. If they tap too, Zuzu lets you both know when you are here at the same time. If only you tap, nobody knows.</p>
          {canTap && (
            <button type="button" onClick={onOpen} className="inline-block mt-2 font-medium underline underline-offset-4">How Meet Again works</button>
          )}
          <p className="mt-2 text-[0.6875rem] opacity-60">Only you can see this tip.</p>
        </div>
      </div>
    </div>
  );
}

// The Meet Again part of the end-of-chat card ("Had a good chat?"), shown under "How did you feel?".
export function MeetEndCard({ isNight, waiting, onOpen }: { isNight: boolean; waiting: boolean; onOpen: () => void }) {
  return (
    <div className={`mt-5 rounded-2xl border p-4 text-center ${isNight ? 'border-emerald-300/25 bg-emerald-300/10' : 'border-emerald-500/25 bg-emerald-50/80'}`}>
      <p className="text-sm font-semibold inline-flex items-center gap-1.5"><MeetIcon className={isNight ? 'text-emerald-300' : 'text-emerald-600'} /> {waiting ? 'Meet Again is on' : 'Had a good chat?'}</p>
      <p className="text-xs opacity-70 mt-1">
        {waiting ? 'If they tap too, Zuzu lets you both know when you are both here.' : 'If you both tap, Zuzu lets you know when you are both here. If only you tap, nobody knows.'}
      </p>
      {!waiting && (
        <button type="button" onClick={onOpen}
          className={`mt-3 w-full rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${isNight ? 'bg-emerald-300 text-black hover:bg-emerald-200' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}>
          <span className="inline-flex items-center gap-2">Meet again <MeetIcon /></span>
        </button>
      )}
    </div>
  );
}

// The Meet Again button in the chat header. Two clear states: soft rose until this person
// taps (the heart beats 3 times when it first shows), then solid green with a check
// (whether or not the other person has tapped yet).
// `label`: for a few seconds it grows into a "Meet again?" pill, so people learn what the button does.
export function MeetMoonButton({ isNight, on, label = false, onClick }: { isNight: boolean; on: boolean; label?: boolean; onClick: () => void }) {
  const off = isNight ? 'border-rose-300/80 bg-rose-400/25 text-rose-200 hover:bg-rose-400/35' : 'border-rose-400/60 bg-rose-100 text-rose-700 hover:bg-rose-200';
  const onChip = isNight ? 'border-emerald-300 bg-emerald-300 text-black hover:bg-emerald-200' : 'border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700';
  return (
    <button type="button" aria-label={on ? 'Meet Again is on' : 'Meet again'} aria-pressed={on} aria-haspopup="dialog" title={on ? 'Meet Again is on' : 'Meet again'} onClick={onClick}
      className={`relative inline-flex items-center rounded-full border transition-all ${on ? onChip : off} ${label ? 'pl-2 pr-3 py-1 gap-1.5' : 'p-1'}`}>
      {/* key: beats again when the "Meet again?" label opens */}
      <HeartHandshake key={label ? 'beat-label' : 'beat'} size={label ? 16 : 18} strokeWidth={2} className={on ? '' : 'meet-heartbeat'} />
      {on && (
        <span aria-hidden="true" className={`absolute -right-1 -bottom-1 flex h-3.5 w-3.5 items-center justify-center rounded-full ring-2 ${isNight ? 'bg-emerald-300 text-black ring-[#0d1b2a]' : 'bg-emerald-600 text-white ring-white'}`}>
          <Check size={9} strokeWidth={3.5} />
        </span>
      )}
      <AnimatePresence initial={false}>
        {label && (
          <motion.span key="meet-label" aria-hidden="true"
            initial={{ width: 0, opacity: 0 }} animate={{ width: 'auto', opacity: 1 }} exit={{ width: 0, opacity: 0 }} transition={{ duration: 0.35 }}
            className="overflow-hidden whitespace-nowrap text-xs font-semibold">
            Meet again?
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  );
}
